import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Users, 
  Gift, 
  Wallet, 
  TrendingUp, 
  Copy, 
  CheckCircle,
  Share2,
  DollarSign,
  Target,
  AlertCircle,
  Download,
  Settings,
  CreditCard
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { supabase } from '../utils/supabase-client';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { ReferralAccountModal } from './ReferralAccountSetup';
import { JWTDebugger } from './JWTDebugger';

interface ReferralStats {
  totalReferrals: number;
  qualifiedReferrals: number;
  pendingReferrals: number;
  earnings: number;
  withdrawn: number;
  balance: number;
  canWithdraw: boolean;
  nextMilestone: {
    at: number;
    remaining: number;
    reward: number;
  };
  withdrawalThreshold: {
    required: number;
    current: number;
    remaining: number;
  };
  qualifiedPercentage: number;
  totalEarnings: number;
}

interface ReferralInfo {
  referralCode: string;
  stats: ReferralStats;
  referrals: any[];
  hasAccount: boolean;
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  };
}

export const ReferralDashboard = () => {
  const [user, setUser] = useState<any>(null);
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        fetchReferralInfo();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      setLoading(false);
    }
  };

  const fetchReferralInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, try to refresh the session to ensure we have a valid token
      console.log('🔄 Refreshing session...');
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.warn('⚠️ Session refresh failed, trying getSession:', refreshError);
        // Fall back to getSession if refresh fails
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('🔐 ReferralDashboard: Getting session...');
        console.log('🔐 Session error:', sessionError);
        console.log('🔐 Session exists:', !!session);
        console.log('🔐 Access token exists:', !!session?.access_token);
        console.log('🔐 Access token length:', session?.access_token?.length);
        
        if (!session?.access_token) {
          console.error('❌ No access token available');
          setError('No active session found. Please sign in again.');
          setLoading(false);
          return;
        }
        
        const tokenPreview = `${session.access_token.substring(0, 20)}...${session.access_token.substring(session.access_token.length - 20)}`;
        console.log('🔐 Token preview:', tokenPreview);

        const apiUrl = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/referral/info`;
        console.log('📡 Calling API:', apiUrl);
        
        // CRITICAL FIX: Send Supabase anon key in Authorization header to satisfy Edge Functions infrastructure
        // AND send the real user token in X-Supabase-Token header for our custom validation
        // This way: Infrastructure validation passes (anon key is always valid)
        //           AND our code gets the real user token for authentication
        console.log('🔐 Sending anon key in Authorization (for infrastructure) + user token in X-Supabase-Token (for app logic)');
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,  // ✅ Anon key satisfies Supabase infrastructure
            'X-Supabase-Token': session.access_token,  // ✅ Real user token for our validation
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API Error Response:', errorText);
          console.error('❌ Response headers:', Object.fromEntries(response.headers.entries()));
          throw new Error(`API request failed (${response.status}): ${errorText}`);
        }

        const result = await response.json();
        // Backend returns data in result.data field
        if (result.success && result.data) {
          setReferralInfo(result.data);
        } else {
          throw new Error(result.error || 'Failed to load referral information');
        }
        return;
      }
      
      // Use the refreshed session
      console.log('✅ Session refreshed successfully');
      console.log('🔐 Refreshed session exists:', !!refreshedSession);
      console.log('🔐 Refreshed access token exists:', !!refreshedSession?.access_token);
      console.log('🔐 Refreshed access token length:', refreshedSession?.access_token?.length);
      
      if (!refreshedSession?.access_token) {
        console.error('❌ No access token available after refresh');
        setError('No active session found. Please sign in again.');
        setLoading(false);
        return;
      }

      const tokenPreview = `${refreshedSession.access_token.substring(0, 20)}...${refreshedSession.access_token.substring(refreshedSession.access_token.length - 20)}`;
      console.log('🔐 Refreshed token preview:', tokenPreview);

      // Backend environment check (non-blocking - for diagnostic purposes only)
      const testEnvUrl = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/test-env`;
      console.log('🧪 Testing backend environment:', testEnvUrl);
      
      try {
        const envResponse = await fetch(testEnvUrl);
        const envResult = await envResponse.json();
        console.log('🧪 Backend environment check:', envResult);
        
        if (envResult.supabaseAnonKey && envResult.supabaseAnonKey.includes('fallback')) {
          console.log('✅ Backend using fallback SUPABASE_ANON_KEY - this is expected and working correctly');
        } else if (envResult.supabaseAnonKey && envResult.supabaseAnonKey.includes('from env')) {
          console.log('✅ Backend using environment SUPABASE_ANON_KEY - optimal configuration');
        } else {
          console.log('ℹ️ Backend SUPABASE_ANON_KEY status:', envResult.supabaseAnonKey || 'Unknown');
        }
      } catch (envError) {
        console.warn('🧪 Failed to test backend environment (non-critical):', envError);
        // Continue anyway - the backend has a fallback mechanism
      }

      const apiUrl = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/referral/info`;
      console.log('📡 Calling API:', apiUrl);
      
      // CRITICAL FIX: Send Supabase anon key in Authorization header to satisfy Edge Functions infrastructure
      // AND send the real user token in X-Supabase-Token header for our custom validation
      // This way: Infrastructure validation passes (anon key is always valid)
      //           AND our code gets the real user token for authentication
      console.log('🔐 Sending anon key in Authorization (for infrastructure) + user token in X-Supabase-Token (for app logic)');
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,  // ✅ Anon key satisfies Supabase infrastructure
          'X-Supabase-Token': refreshedSession.access_token,  // ✅ Real user token for our validation
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        console.error('❌ Response headers:', Object.fromEntries(response.headers.entries()));
        
        // If we get a 401, check if it's specifically a JWT error
        if (response.status === 401 && errorText.includes('Invalid JWT')) {
          console.error('❌ JWT validation failed on backend');
          console.error('❌ This usually means:');
          console.error('   1. The Supabase project ID or keys don\'t match between frontend and backend');
          console.error('   2. The SUPABASE_ANON_KEY is not set in the Edge Function environment');
          console.error('   3. The JWT was signed by a different Supabase project');
          
          // Test if the referral test endpoint works (no auth required)
          try {
            console.log('🧪 Testing no-auth endpoint to verify backend is reachable...');
            const testUrl = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/referral/test`;
            const testResponse = await fetch(testUrl);
            const testResult = await testResponse.json();
            console.log('🧪 No-auth test result:', testResult);
            
            if (testResult.success) {
              setError('Authentication error: Your session token is not being accepted by the backend. This may be a configuration issue. Please try signing out and signing in again.');
            } else {
              setError('Backend connection error. Please try again later.');
            }
          } catch (testError) {
            console.error('🧪 No-auth test also failed:', testError);
            setError('Cannot connect to backend. Please check your internet connection.');
          }
          
          setLoading(false);
          return;
        }
        
        throw new Error(`API request failed (${response.status}): ${errorText}`);
      }

      const result = await response.json();
      // Backend returns data in result.data field
      if (result.success && result.data) {
        setReferralInfo(result.data);
      } else {
        throw new Error(result.error || 'Failed to load referral information');
      }
    } catch (error: any) {
      console.error('Error fetching referral info:', error);
      setError(error.message || 'Failed to load referral information');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    const link = `${window.location.origin}?ref=${referralInfo?.referralCode}`;
    
    try {
      // Try modern clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback for older browsers or insecure contexts
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.log('ℹ️ Copy failed, trying fallback method');
      try {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopied(true);
          toast.success('Referral link copied!');
          setTimeout(() => setCopied(false), 2000);
        } else {
          toast.error('Failed to copy link');
        }
      } catch (fallbackError) {
        toast.error('Failed to copy link');
      }
    }
  };

  const shareReferralLink = async () => {
    const link = `${window.location.origin}?ref=${referralInfo?.referralCode}`;
    const text = `Join Sentlo and help me earn! Sign up using my referral code: ${referralInfo?.referralCode}

${link}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join Sentlo', text });
      } catch (error) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Please log in to view your referral dashboard.</p>
      </div>
    );
  }

  if (error || !referralInfo) {
    return (
      <div className="max-w-3xl mx-auto p-8 space-y-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-gray-800 font-semibold mb-2">Unable to load referral dashboard</p>
          {error && (
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              Error: {error}
            </p>
          )}
          <div className="flex gap-2 justify-center">
            <Button 
              onClick={fetchReferralInfo}
              className="mt-4"
            >
              Retry
            </Button>
            <Button 
              onClick={() => setShowDebug(!showDebug)}
              variant="outline"
              className="mt-4"
            >
              {showDebug ? 'Hide' : 'Show'} Debug Tools
            </Button>
          </div>
        </div>
        
        {showDebug && (
          <div className="mt-6">
            <JWTDebugger />
          </div>
        )}
      </div>
    );
  }

  const stats = referralInfo.stats;
  const milestoneProgress = (stats.qualifiedReferrals % 50) / 50 * 100;
  const withdrawalProgress = (stats.withdrawalThreshold.current / stats.withdrawalThreshold.required) * 100;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-3xl md:text-4xl font-bold mb-2">💎 Referral Program</h1>
        <p className="text-gray-600">Earn ₦10,000 for every 50 friends who join and post!</p>
      </motion.div>

      {/* Account Setup Banner */}
      {!referralInfo.hasAccount && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-shrink-0 p-3 bg-amber-100 rounded-full">
                <CreditCard className="w-8 h-8 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1 text-amber-900">
                  Setup Your Payment Account
                </h3>
                <p className="text-amber-700 text-sm">
                  Add your bank details now to start earning. You'll need this to receive payments when you reach 150 qualified referrals.
                </p>
              </div>
              <Button
                onClick={() => setShowAccountSetup(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Setup Now
              </Button>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Account Setup Complete Badge */}
      {referralInfo.hasAccount && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 p-2 bg-green-100 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-green-900 text-sm">
                  ✓ Account details verified
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="w-6 h-6" />}
          title="Total Referrals"
          value={stats.totalReferrals}
          color="blue"
        />
        <StatCard
          icon={<CheckCircle className="w-6 h-6" />}
          title="Qualified Referrals"
          value={stats.qualifiedReferrals}
          subtitle="(Have posted)"
          color="green"
        />
        <StatCard
          icon={<DollarSign className="w-6 h-6" />}
          title="Total Earnings"
          value={`₦${stats.earnings.toLocaleString()}`}
          color="purple"
        />
        <StatCard
          icon={<Wallet className="w-6 h-6" />}
          title="Available Balance"
          value={`₦${stats.balance.toLocaleString()}`}
          subtitle={stats.canWithdraw ? 'Ready!' : `Need ₦${(10000 - stats.balance).toLocaleString()}`}
          color="orange"
        />
      </div>

      {/* Referral Link & Code Card */}
      <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Gift className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="font-bold text-lg">Your Referral Link & Code</h3>
                <p className="text-sm text-gray-600">Share with friends to earn rewards</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Referral Link:</label>
              <div className="flex gap-2">
                <div className="flex-1 bg-white p-3 rounded-lg border-2 border-green-300 flex items-center">
                  <p className="text-sm font-mono text-gray-800 truncate">
                    {`${window.location.origin}?ref=${referralInfo?.referralCode}`}
                  </p>
                </div>
                <Button
                  onClick={copyReferralLink}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
                <Button
                  onClick={shareReferralLink}
                  size="sm"
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </Button>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-600 mb-1 block">Your Referral Code:</label>
              <div className="bg-white px-4 py-3 rounded-lg border-2 border-green-300 inline-block">
                <p className="text-2xl font-bold text-green-700 tracking-wider">
                  {referralInfo?.referralCode}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Progress Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Milestone Progress */}
        <Card className="p-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Next Milestone Reward</h3>
              <Target className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Progress to {stats.nextMilestone.at} qualified referrals</span>
                <span className="font-bold">{stats.nextMilestone.at - stats.nextMilestone.remaining} / {stats.nextMilestone.at}</span>
              </div>
              <Progress value={milestoneProgress} className="h-3" />
            </div>
            <p className="text-sm text-gray-600">
              {stats.nextMilestone.remaining} more qualified referrals to earn{' '}
              <span className="font-bold text-green-600">₦{stats.nextMilestone.reward.toLocaleString()}</span>
            </p>
          </div>
        </Card>

        {/* Withdrawal Threshold */}
        <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Withdrawal Eligibility</h3>
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Progress to {stats.withdrawalThreshold.required} qualified referrals</span>
                <span className="font-bold">{stats.withdrawalThreshold.current} / {stats.withdrawalThreshold.required}</span>
              </div>
              <Progress value={withdrawalProgress} className="h-3" />
            </div>
            {stats.canWithdraw ? (
              <p className="text-sm text-green-600 font-semibold">✅ You can now withdraw!</p>
            ) : (
              <p className="text-sm text-gray-600">
                {stats.withdrawalThreshold.remaining} more qualified referrals needed
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Withdrawal Button */}
      {stats.canWithdraw && stats.balance >= 10000 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Button
            onClick={() => setShowWithdrawal(true)}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
          >
            <Wallet className="w-6 h-6 mr-2" />
            Withdraw ₦{stats.balance.toLocaleString()}
          </Button>
        </motion.div>
      )}

      {/* Referrals List */}
      {referralInfo.referrals && referralInfo.referrals.length > 0 && (
        <Card className="p-6">
          <h3 className="font-bold text-lg mb-4">Your Referrals</h3>
          <div className="space-y-3">
            {referralInfo.referrals.map((referral: any, index: number) => (
              <div
                key={referral.id || index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold">
                    {referral.name ? referral.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div>
                    <p className="font-medium">{referral.name || 'User'}</p>
                    <p className="text-xs text-gray-600">
                      Joined {new Date(referral.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {referral.isQualified ? (
                    <span className="text-green-600 font-semibold flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Qualified
                    </span>
                  ) : (
                    <span className="text-amber-600 text-sm">Pending</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Account Setup Modal */}
      <ReferralAccountModal
        isOpen={showAccountSetup}
        onClose={() => setShowAccountSetup(false)}
        onComplete={fetchReferralInfo}
        existingDetails={referralInfo.bankDetails}
      />
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600'
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="relative overflow-hidden rounded-xl bg-white shadow-lg"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${colors[color]} opacity-10`}></div>
      <div className="relative p-6">
        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${colors[color]} text-white mb-3`}>
          {icon}
        </div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold mb-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </motion.div>
  );
};